// frontend/src/screens/admin/sps/StaffDirectoryEditScreen.jsx
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Button, Form, Alert, Spinner,
  Tab, Tabs, Table, Badge,
} from 'react-bootstrap';

import {
  useGetStaffByPpsQuery,
  useUpdateStaffMemberMutation,
  useGetEmailHistoryQuery,
  useGetPayRecordsByPpsQuery,
  useRunIndividualMutation,
  useGetRunHistoryQuery,
  useSendToOneMutation,
} from '../../../slices/spsApiSlices';



// ── API base URL — works in dev (port 5800) and production (same host) ──────
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:5800'
  : '';

const CIVIL_OPTIONS = [
  '', 'Single', 'Married', 'Civil Partnered', 'Co-Habiting',
  'Separated', 'Divorced', 'Widowed', 'Surviving Civil Partner', 'A former Civil Partner',
];

export default function StaffDirectoryEditScreen() {
  const { pps }   = useParams();
  const navigate  = useNavigate();

  const { data: member, isLoading, isError, refetch } = useGetStaffByPpsQuery(pps);
  const [updateMember, { isLoading: saving }] = useUpdateStaffMemberMutation();
  const [runOne,  { isLoading: running }]     = useRunIndividualMutation();
  const [sendOne, { isLoading: emailing }]    = useSendToOneMutation();

  const { data: emailHistory = [] }           = useGetEmailHistoryQuery(pps);
  const { data: payData }                     = useGetPayRecordsByPpsQuery(pps);
  const { data: runHistory }                  = useGetRunHistoryQuery({ type: 'individual' });

  const [form,     setForm]     = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Initialise form from loaded member data (once)
  if (member && !form) {
    setForm({
      email:                   member.email || '',
      dateOfBirth:             member.dateOfBirth             ? member.dateOfBirth.substring(0, 10) : '',
      normalRetirementDate:    member.normalRetirementDate    ? member.normalRetirementDate.substring(0, 10) : '',
      addressLine1:            member.addressLine1 || '',
      addressLine2:            member.addressLine2 || '',
      addressLine3:            member.addressLine3 || '',
      addressLine4:            member.addressLine4 || '',
      city:                    member.city         || '',
      county:                  member.county       || '',
      eircode:                 member.eircode      || '',
      civilStatus:             member.civilStatus  || '',
      pensionsAdjustmentOrder: member.pensionsAdjustmentOrder || '',
      membershipCategory:      member.membershipCategory || 'Standard accrual',
      isActive:                member.isActive !== false,
      sendEmail:               member.sendEmail !== false,
      notes:                   member.notes || '',
    });
  }

  const handleSave = async () => {
    try {
      await updateMember({ pps, ...form }).unwrap();
      setFeedback({ type: 'success', msg: 'Saved successfully' });
      refetch();
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Save failed' });
    }
  };

  const handleRun = async () => {
    setFeedback(null);
    try {
      const res = await runOne(pps).unwrap();
      setFeedback({ type: 'success', msg: `✓ Certificate generated for ${res.runYear}. Files are ready to download.`, files: res.files, runYear: res.runYear });
      refetch();
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Calculator run failed' });
    }
  };

  const handleEmail = async () => {
    setFeedback(null);
    try {
      await sendOne(pps).unwrap();
      setFeedback({ type: 'success', msg: `✓ Email sent to ${form?.email || member?.email}` });
      refetch();
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Email failed' });
    }
  };

  if (isLoading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  if (isError)   return <Alert variant="danger" className="m-3">Member not found — PPS: {pps}</Alert>;
  if (!form)     return null;

  //const upd = (field, val) => setForm({ ...form, [field]: val });

  const upd = (field, val) => {
  const next = { ...form, [field]: val };
  if (field === 'dateOfBirth' && val) {
    const dob = new Date(val);
    if (!isNaN(dob.getTime())) {
      const nrd = new Date(dob);
      nrd.setFullYear(nrd.getFullYear() + 66);
      next.normalRetirementDate = nrd.toISOString().substring(0, 10);
    }
  }
  setForm(next);
};


  // Pay records grouped by year
  const payByYear = {};
  (payData || []).forEach((r) => {
    if (!payByYear[r.year]) payByYear[r.year] = { records: 0, pay: 0, allow: 0 };
    payByYear[r.year].records++;
    payByYear[r.year].pay   += r.grossBasicPensionablePay;
    payByYear[r.year].allow += r.grossBasicPensionableAllowances;
  });

  // Filter run history to this member
  const memberRuns = (runHistory?.runs || []).filter((r) => r.ppsNumber === pps);

  const lastRunYear = member.lastStatementYear;

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <Button variant="link" className="p-0 me-2 text-muted" onClick={() => navigate('/admin/sps/staff')}>
            ← Staff Directory
          </Button>
          <h4 className="mb-0 mt-1">{member.employeeName}</h4>
          <small className="text-muted" style={{ fontFamily: 'monospace' }}>{pps}</small>
          {!member.isActive && <Badge bg="secondary" className="ms-2">Leaver</Badge>}
        </Col>
        <Col xs="auto" className="d-flex gap-2 flex-wrap">
          <Button
            variant="success"
            size="sm"
            onClick={handleRun}
            disabled={running}
          >
            {running ? <><Spinner size="sm" animation="border" className="me-1" /> Running…</> : '▶ Generate Certificate'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleEmail}
            disabled={emailing || !form.email || !lastRunYear}
            title={!form.email ? 'No email address — add one below' : !lastRunYear ? 'Run the calculator first' : ''}
          >
            {emailing ? <><Spinner size="sm" animation="border" className="me-1" /> Sending…</> : '✉ Send Email'}
          </Button>
          {lastRunYear && (
            <>
              <a href={`${API_BASE}/api/calculator/download/${lastRunYear}/${member.employeeName.replace(/[^a-zA-Z0-9]/g,'_')}_${pps}.xlsx`}
                className="btn btn-sm btn-outline-secondary" target="_blank" rel="noreferrer">
                ⬇ Excel
              </a>
              <a href={`${API_BASE}/api/calculator/download/${lastRunYear}/Annual-Benefit-Statement-${member.employeeName.replace(/[^a-zA-Z0-9]/g,'_')}_${pps}.html`}
                className="btn btn-sm btn-outline-secondary" target="_blank" rel="noreferrer">
                ⬇ ABS
              </a>
              <a href={`${API_BASE}/api/calculator/download/${lastRunYear}/${member.employeeName.replace(/[^a-zA-Z0-9]/g,'_')}_${pps}.html`}
                className="btn btn-sm btn-outline-secondary" target="_blank" rel="noreferrer">
                ⬇ CPI
              </a>
            </>
          )}
        </Col>
      </Row>

      {feedback && (
        <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)} className="mb-3">
          {feedback.msg}
          {feedback.files && (
            <div className="mt-2 d-flex gap-2 flex-wrap">
              <a href={`${API_BASE}/api/calculator/download/${feedback.runYear}/${feedback.files.xlsx}`}
                className="btn btn-sm btn-outline-success" target="_blank" rel="noreferrer">⬇ Excel</a>
              <a href={`${API_BASE}/api/calculator/download/${feedback.runYear}/${feedback.files.absHtml}`}
                className="btn btn-sm btn-outline-primary" target="_blank" rel="noreferrer">⬇ Annual Benefit Statement</a>
              <a href={`${API_BASE}/api/calculator/download/${feedback.runYear}/${feedback.files.cpiHtml}`}
                className="btn btn-sm btn-outline-secondary" target="_blank" rel="noreferrer">⬇ CPI Summary</a>
            </div>
          )}
        </Alert>
      )}

      <Tabs defaultActiveKey="details" className="mb-3">

        {/* ── Details tab ── */}
        <Tab eventKey="details" title="Personal Details (ABS Part A)">
          <Row className="g-3">
            <Col md={6}>
              <Card>
                <Card.Header className="fw-semibold bg-light">Contact</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Email address <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} />
                    <Form.Text className="text-muted">Used for statement delivery</Form.Text>
                  </Form.Group>
                  <Row className="g-2 mb-3">
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Address line 1</Form.Label>
                      <Form.Control size="sm" value={form.addressLine1} onChange={(e) => upd('addressLine1', e.target.value)} />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Address line 2</Form.Label>
                      <Form.Control size="sm" value={form.addressLine2} onChange={(e) => upd('addressLine2', e.target.value)} />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Address line 3</Form.Label>
                      <Form.Control size="sm" value={form.addressLine3} onChange={(e) => upd('addressLine3', e.target.value)} />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Address line 4</Form.Label>
                      <Form.Control size="sm" value={form.addressLine4} onChange={(e) => upd('addressLine4', e.target.value)} />
                    </Col>
                  </Row>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">City</Form.Label>
                      <Form.Control size="sm" value={form.city} onChange={(e) => upd('city', e.target.value)} />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">County</Form.Label>
                      <Form.Control size="sm" value={form.county} onChange={(e) => upd('county', e.target.value)} />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Eircode</Form.Label>
                      <Form.Control size="sm" value={form.eircode} onChange={(e) => upd('eircode', e.target.value)} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-3">
                <Card.Header className="fw-semibold bg-light">ABS Part A Fields</Card.Header>
                <Card.Body>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Date of Birth</Form.Label>
                      <Form.Control size="sm" type="date" value={form.dateOfBirth} onChange={(e) => upd('dateOfBirth', e.target.value)} />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-semibold">Normal Retirement Date</Form.Label>
                      <Form.Control size="sm" type="date" value={form.normalRetirementDate} onChange={(e) => upd('normalRetirementDate', e.target.value)} />
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small fw-semibold">Civil Status</Form.Label>
                      <Form.Select size="sm" value={form.civilStatus} onChange={(e) => upd('civilStatus', e.target.value)}>
                        {CIVIL_OPTIONS.map((o) => <option key={o} value={o}>{o || '— Select —'}</option>)}
                      </Form.Select>
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small fw-semibold">Pensions Adjustment Order</Form.Label>
                      <Form.Select size="sm" value={form.pensionsAdjustmentOrder} onChange={(e) => upd('pensionsAdjustmentOrder', e.target.value)}>
                        <option value="">— Select —</option>
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </Form.Select>
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small fw-semibold">Membership Category</Form.Label>
                      <Form.Control size="sm" value={form.membershipCategory} onChange={(e) => upd('membershipCategory', e.target.value)} />
                    </Col>
                    <Col xs={12} className="mt-1">
                      <Form.Check
                        type="switch"
                        id="isActive"
                        label="Active member (uncheck = leaver)"
                        checked={form.isActive}
                        onChange={(e) => upd('isActive', e.target.checked)}
                      />
                    </Col>
                    <Col xs={12}>
                      <Form.Check
                        type="switch"
                        id="sendEmail"
                        label="Send email (uncheck to suppress statement delivery)"
                        checked={form.sendEmail}
                        onChange={(e) => upd('sendEmail', e.target.checked)}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="fw-semibold bg-light">Scheme Info (read-only from pay data)</Card.Header>
                <Card.Body className="small">
                  <Row className="g-1">
                    {[
                      ['Scheme start year', member.schemeStartYear || '—'],
                      ['Last pay year', member.lastPayYear || '—'],
                      ['Payment frequency', member.paymentFrequency || '—'],
                      ['Last cert year', member.lastStatementYear || 'Not generated'],
                      ['Last email', member.lastEmailSent ? new Date(member.lastEmailSent).toLocaleDateString('en-IE') : 'Not sent'],
                    ].map(([label, val]) => (
                      <Col xs={6} key={label}>
                        <div className="text-muted">{label}</div>
                        <div className="fw-semibold">{val}</div>
                      </Col>
                    ))}
                    <Col xs={12} className="mt-2">
                      <Form.Check
                        type="switch"
                        id="isSingleScheme"
                        label="Single Public Service Pension Scheme member"
                        checked={member.isSingleScheme || false}
                        disabled
                      />
                      <Form.Text className="text-muted">
                        Derived from pay data — edit via Import
                      </Form.Text>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Admin notes</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} />
              </Form.Group>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving…</> : 'Save Changes'}
              </Button>
            </Col>
          </Row>
        </Tab>

        {/* ── Pay Summary tab ── */}
        <Tab eventKey="pay" title={`Pay Summary (${Object.keys(payByYear).length} years)`}>
          <div className="table-responsive">
            <Table bordered size="sm" style={{ fontSize: '.83rem' }}>
              <thead className="table-dark">
                <tr>
                  <th>Year</th>
                  <th className="text-end">Records</th>
                  <th className="text-end">Gross Pay</th>
                  <th className="text-end">Allowances</th>
                  <th className="text-end">Total Pay</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(payByYear).sort(([a],[b]) => a-b).map(([yr, d]) => (
                  <tr key={yr}>
                    <td className="fw-semibold">{yr}</td>
                    <td className="text-end">{d.records}</td>
                    <td className="text-end">€{d.pay.toFixed(2)}</td>
                    <td className="text-end">€{d.allow.toFixed(2)}</td>
                    <td className="text-end fw-semibold" style={{ color: d.pay+d.allow < 0 ? '#dc3545' : undefined }}>
                      €{(d.pay + d.allow).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Tab>

        {/* ── Run History tab ── */}
        <Tab eventKey="runs" title="Run History">
          {memberRuns.length === 0 ? (
            <Alert variant="secondary" className="mt-2">No individual runs recorded for this member yet.</Alert>
          ) : (
            <Table bordered size="sm" style={{ fontSize: '.83rem' }}>
              <thead className="table-dark">
                <tr><th>Date</th><th>Year</th><th>Status</th><th>Triggered by</th></tr>
              </thead>
              <tbody>
                {memberRuns.map((r) => (
                  <tr key={r._id}>
                    <td>{new Date(r.startedAt).toLocaleString('en-IE')}</td>
                    <td>{r.runYear}</td>
                    <td><Badge bg={r.status==='completed'?'success':r.status==='error'?'danger':'warning'}>{r.status}</Badge></td>
                    <td>{r.triggeredBy?.name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        {/* ── Email History tab ── */}
        <Tab eventKey="email" title="Email History">
          {emailHistory.length === 0 ? (
            <Alert variant="secondary" className="mt-2">No emails sent to this member yet.</Alert>
          ) : (
            <Table bordered size="sm" style={{ fontSize: '.83rem' }}>
              <thead className="table-dark">
                <tr><th>Date sent</th><th>Year</th><th>To</th><th>Attachments</th><th>Status</th></tr>
              </thead>
              <tbody>
                {emailHistory.map((e) => (
                  <tr key={e._id}>
                    <td>{new Date(e.sentAt).toLocaleString('en-IE')}</td>
                    <td>{e.statementYear}</td>
                    <td style={{ fontSize: '.75rem' }}>{e.emailAddress}</td>
                    <td style={{ fontSize: '.72rem' }}>{(e.attachments||[]).join(', ')}</td>
                    <td>
                      <Badge bg={e.status==='sent'?'success':e.status==='failed'?'danger':'warning'}>
                        {e.status}
                      </Badge>
                      {e.errorMessage && <div className="text-danger" style={{ fontSize: '.7rem' }}>{e.errorMessage}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

      </Tabs>
    </Container>
  );
}
